import { apiClient } from '../../lib/apiClient';
import { createCollectionApi } from '../knowledge-base/knowledgeBaseApi';
import type { CollectionResult } from '../knowledge-base/useCollection';
import type {
  CuratedFeedRead,
  FeedCategoryCreate,
  FeedCategoryRead,
  FeedCategoryUpdate,
  FeedCurationCreate,
  FeedCurationRunRead,
  FeedSourceCreate,
  FeedSourceRead,
  FeedSourceUpdate,
  FeedSourcesFromCatalog,
  NewsFeedRead,
} from '../../types/api';

/**
 * The industry newsfeed: what is being said in this business's field, and the
 * feeds it is read out of.
 *
 * Nothing on this surface reaches the pipeline. Reading the news spends no
 * generation, and neither does following one more source.
 */

/** Like every owned resource, the feed is business-scoped (SRS §4). */
function businessPath(businessId: string, segment: string): string {
  return `/api/v1/businesses/${businessId}/${segment}`;
}

/**
 * Headlines for this business, newest first.
 *
 * `refresh: false` serves the cached pull, which is what a page open wants;
 * `refresh: true` goes back out to the sources, which is what the refresh
 * button asks for. Neither costs a run.
 */
export function fetchNews(
  businessId: string,
  refresh: boolean,
  signal?: AbortSignal,
): Promise<NewsFeedRead> {
  return apiClient.get<NewsFeedRead>(businessPath(businessId, 'news'), {
    query: { refresh },
    signal,
  });
}

/**
 * The feeds behind that list — auto-discovered from the brand's industry, plus
 * whatever the user has pasted in.
 *
 * The same REST shape as every knowledge-base collection, so it rides the same
 * factory and gets `useCollection`'s undo-window delete for free. The update is
 * a PATCH carrying one field: a source is followed or it is not, and the only
 * thing about it the user can change is which category it is filed under.
 */
export const feedSourcesApi = createCollectionApi<
  FeedSourceRead,
  FeedSourceCreate,
  FeedSourceUpdate
>('feed-sources', { updateMethod: 'PATCH' });

/**
 * The one sources collection `/news` holds, as the surfaces that share it see
 * it: the Edit feed dialog and its four tabs, the filter select and the sources
 * list. Its `update` is the move between categories and nothing else.
 */
export type FeedSourceCollection = CollectionResult<
  FeedSourceRead,
  FeedSourceCreate,
  FeedSourceUpdate
>;

/* ---------------------------------------------------------------- */
/* Categories — the user's own folders over those sources            */
/* ---------------------------------------------------------------- */

/**
 * Folders of followed sources, in the order the list endpoint gives them.
 *
 * Another business-scoped collection on the same REST shape, so the same
 * factory again — with PATCH behind `update`, because a rename sends `name`
 * alone. Deleting one is a plain DELETE: the sources it held stay followed and
 * fall back to the uncategorised bucket, which is why this delete is a
 * confirmation rather than an undo window — nothing is lost to undo.
 */
export const feedCategoriesApi = createCollectionApi<
  FeedCategoryRead,
  FeedCategoryCreate,
  FeedCategoryUpdate
>('feed-categories', { updateMethod: 'PATCH' });

/** The one categories collection `/news` holds, shared like the sources one. */
export type FeedCategoryCollection = CollectionResult<
  FeedCategoryRead,
  FeedCategoryCreate,
  FeedCategoryUpdate
>;

/* ---------------------------------------------------------------- */
/* The curated catalogue — a hand-checked list, the same for everyone */
/* ---------------------------------------------------------------- */

/**
 * Every publication we offer, ordered by industry and then by position inside
 * it. Global like `/vibes` and `/slide-themes`: it is a catalogue, not a record
 * of this business, so it carries no `business_id` and never changes per user.
 */
export function fetchFeedCatalog(signal?: AbortSignal): Promise<CuratedFeedRead[]> {
  return apiClient.get<CuratedFeedRead[]>('/api/v1/feed-catalog', { signal });
}

/**
 * Follow a batch of catalogue entries.
 *
 * The 201 carries only the rows that were newly created: a feed the business
 * already follows is skipped rather than refused, so a partial overlap is a
 * success with a shorter list. `CATALOG_FEED_NOT_FOUND` (404) means one of the
 * ids has left the published catalogue.
 */
export function addFromCatalog(
  businessId: string,
  catalogIds: readonly string[],
): Promise<FeedSourceRead[]> {
  const body: FeedSourcesFromCatalog = { catalog_ids: [...catalogIds] };
  return apiClient.post<FeedSourceRead[]>(businessPath(businessId, 'feed-sources/from-catalog'), {
    body,
  });
}

/* ---------------------------------------------------------------- */
/* Curation runs — the one model call on this surface                */
/* ---------------------------------------------------------------- */

/**
 * Ask for sources in the user's own words.
 *
 * Comes back `pending`: a worker does the looking, so the caller polls
 * {@link fetchFeedCuration} until the status moves. A second run while one is
 * outstanding is 409 `FEED_CURATION_PENDING`; a queue that is down is 503.
 *
 * This reaches a model. It still spends no generation — the monthly quota
 * counts pipeline runs, and nothing here starts one.
 */
export function createFeedCuration(
  businessId: string,
  prompt: string,
): Promise<FeedCurationRunRead> {
  const body: FeedCurationCreate = { prompt };
  return apiClient.post<FeedCurationRunRead>(businessPath(businessId, 'feed-curation'), { body });
}

/** One run, by id. Polled while `status` is `pending`. */
export function fetchFeedCuration(
  businessId: string,
  runId: string,
  signal?: AbortSignal,
): Promise<FeedCurationRunRead> {
  return apiClient.get<FeedCurationRunRead>(
    `${businessPath(businessId, 'feed-curation')}/${runId}`,
    { signal },
  );
}

/**
 * Past runs, newest first. Asked for with `limit: 1` after a 409, which is how
 * the dialog finds the run it is being told to wait for.
 */
export function listFeedCurations(
  businessId: string,
  limit: number,
  signal?: AbortSignal,
): Promise<FeedCurationRunRead[]> {
  return apiClient.get<FeedCurationRunRead[]>(businessPath(businessId, 'feed-curation'), {
    query: { limit },
    signal,
  });
}
